/**
 * TransferHistory Component
 * 송금 관리 페이지 - 본사에서 협력사로 송금
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTransferData } from '../hooks/useTransferData';
import { useDelayedLoading } from '@/shared/hooks/useDelayedLoading';
import { usePagination } from '@/shared/hooks/usePagination';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import Loading from '@/shared/components/feedback/Loading';
import TransferModal from './TransferModal';
import FilterSection from './FilterSection';
import HistoryTable from './HistoryTable';
import SummarySection from './SummarySection';
import {
    TransferHistory as TransferHistoryType,
    TransferFilters,
    createDefaultTransferFilters
} from '../types/transfer.types';
import '@/shared/styles/common-table.css';
import '../styles/TransferHistory.css';

// Constants
const TRANSFER_ITEMS_PER_PAGE = 20;
const STORAGE_KEY_TRANSFER_FILTERS = 'transfer_management_filters';

// Types
type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
    columnKey: string | null;
    direction: SortDirection;
}

const TransferHistory: React.FC = () => {
    // ======== Hooks ========
    const {
        histories,
        totalTransferAmt,
        supplierOptions,
        loading,
        excelDownloading,
        fetchHistories,
        fetchSupplierOptions,
        requestTransfer,
        downloadExcel
    } = useTransferData();

    const showLoading = useDelayedLoading(loading);

    // 정렬 상태
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        columnKey: null,
        direction: null
    });

    /**
     * 정렬 핸들러
     */
    const handleSort = useCallback((columnKey: string) => {
        setSortConfig((prev) => {
            if (prev.columnKey === columnKey) {
                if (prev.direction === 'asc') return { columnKey, direction: 'desc' };
                if (prev.direction === 'desc') return { columnKey: null, direction: null };
            }
            return { columnKey, direction: 'asc' };
        });
    }, []);

    /**
     * 정렬된 전체 데이터
     */
    const sortedHistories = useMemo(() => {
        if (!histories || !sortConfig.columnKey || !sortConfig.direction) {
            return histories || [];
        }

        return [...histories].sort((a, b) => {
            const key = sortConfig.columnKey as keyof TransferHistoryType;
            const aVal = a[key];
            const bVal = b[key];

            // null/undefined 처리
            const aEmpty = aVal == null || aVal === '';
            const bEmpty = bVal == null || bVal === '';

            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return sortConfig.direction === 'asc' ? 1 : -1;
            if (bEmpty) return sortConfig.direction === 'asc' ? -1 : 1;

            // 숫자 비교
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // 문자열 비교
            const aStr = String(aVal);
            const bStr = String(bVal);
            return sortConfig.direction === 'asc'
                ? aStr.localeCompare(bStr)
                : bStr.localeCompare(aStr);
        });
    }, [histories, sortConfig]);

    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: sortedHistories, itemsPerPage: TRANSFER_ITEMS_PER_PAGE });

    // ======== State ========
    /** 검색 필터 상태 */
    const [filters, setFilters] = useState<TransferFilters>(() => {
        const saved = loadFilters<TransferFilters>(STORAGE_KEY_TRANSFER_FILTERS);
        return saved || createDefaultTransferFilters();
    });

    /** 송금 모달 열림 상태 */
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

    /** 초기화 완료 여부 */
    const hasInitialized = useRef(false);

    // ======== Effects ========
    /** 초기 데이터 로드 */
    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            const savedFilters = loadFilters<TransferFilters>(STORAGE_KEY_TRANSFER_FILTERS);
            const filtersToUse = savedFilters || filters;

            fetchHistories(
                filtersToUse.startDate,
                filtersToUse.endDate,
                filtersToUse.suppId || undefined
            ).catch(() => { /* 에러는 훅에서 처리 */ });

            // 협력사 목록 조회
            fetchSupplierOptions().catch(() => { /* 에러는 훅에서 처리 */ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // ======== Event Handlers ========
    /** 검색 실행 */
    const handleSearch = useCallback(() => {
        resetPagination();
        fetchHistories(
            filters.startDate,
            filters.endDate,
            filters.suppId || undefined
        );
        saveFilters(STORAGE_KEY_TRANSFER_FILTERS, filters);
    }, [filters, resetPagination, fetchHistories]);

    /** 송금 처리 */
    const handleRequestTransfer = useCallback(async (
        suppId: string,
        amount: number,
        memo?: string
    ) => {
        const result = await requestTransfer(suppId, amount, memo);
        if (result) {
            setIsTransferModalOpen(false);
            // 송금 내역 갱신
            fetchHistories(
                filters.startDate,
                filters.endDate,
                filters.suppId || undefined
            );
        }
    }, [requestTransfer, filters, fetchHistories]);

    /** 송금 모달 열기 */
    const handleTransferModalOpen = useCallback(() => {
        setIsTransferModalOpen(true);
    }, []);

    /** 엑셀 다운로드 */
    const handleExcelDownload = useCallback(() => {
        downloadExcel(
            filters.startDate,
            filters.endDate,
            filters.suppId || undefined
        );
    }, [filters, downloadExcel]);

    // ======== Render ========
    if (showLoading) {
        return <Loading />;
    }

    return (
        <div className="transfer-history">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">송금 관리</h1>
            </div>

            {/* 검색 필터 섹션 */}
            <FilterSection
                filters={filters}
                supplierOptions={supplierOptions}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onTransferClick={handleTransferModalOpen}
                onExcelDownload={handleExcelDownload}
                excelDownloading={excelDownloading}
            />

            {/* 합계 섹션 */}
            <SummarySection
                totalCount={histories.length}
                totalTransferAmt={totalTransferAmt}
            />

            {/* 검색 결과 건수 */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                총 <strong>{sortedHistories.length.toLocaleString()}건</strong>
            </div>

            {/* 송금 내역 테이블 */}
            <HistoryTable
                histories={sortedHistories}
                currentItems={currentItems}
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={TRANSFER_ITEMS_PER_PAGE}
                onPageChange={goToPage}
                sortConfig={sortConfig}
                onSort={handleSort}
            />

            {/* 송금 모달 */}
            {isTransferModalOpen && (
                <TransferModal
                    supplierOptions={supplierOptions}
                    onClose={() => setIsTransferModalOpen(false)}
                    onRequest={handleRequestTransfer}
                />
            )}
        </div>
    );
};

export default TransferHistory;
